import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  FaFilePdf,
  FaDownload,
  FaEye,
} from 'react-icons/fa';
import annualReportPDF from '../Assets/Annual Report 2024-25.pdf';
import shivpratapLogo from '../Assets/SHIVPRATAP LOGO.png';
import engLogo from '../Assets/eng logo.jpeg';

const AnnualReport = () => {
  const { language } = useLanguage();
  const [loadingId, setLoadingId] = useState(null);

  // Translations
  const translations = {
    en: {
      pageTitle: 'Annual Reports',
      pdfPreview: 'PDF Preview',
      availableForAll: 'Available for everyone',
      published: 'Published:',
      updated: 'Updated:',
      previewTitle: 'PDF Preview',
      previewDescription: 'Use the buttons below to download or view online',
      viewOnline: 'View Online',
      download: 'Download',
      loading: 'Loading...',
      pdfDownload: 'PDF Download',
      onlineRead: 'Online Read',
      mobileComputer: 'Mobile/Computer'
    },
    mr: {
      pageTitle: 'वार्षिक अहवाल',
      pdfPreview: '',
      availableForAll: '',
      published: '',
      updated: '',
      previewTitle: 'PDF प्रिव्यू',
      previewDescription: 'डाउनलोड किंवा ऑनलाइन पाहण्यासाठी खालील बटण वर क्लिक करा.',
      viewOnline: 'ऑनलाइन पहा',
      download: 'डाउनलोड करा',
      loading: 'लोड होत आहे...',
      pdfDownload: 'PDF डाउनलोड',
      onlineRead: 'ऑनलाइन वाचा',
      mobileComputer: 'मोबाईल/कॉम्प्युटर'
    }
  };

  const t = translations[language];

  const reports = [
    {
      id: '2025-26',
      year: '2025-26',
      titleEn: 'Annual Report 2025-26',
      titleMr: 'वार्षिक अहवाल २०२५-२६',
      descriptionEn: '24th Annual Report of Shivpratap Multistate Institution',
      descriptionMr: 'शिवप्रताप मल्टीस्टेट संस्थेचा २४वा वार्षिक अहवाल',
      fileName: 'Annual Report 2025-2026.pdf',
      pdfPath: process.env.PUBLIC_URL + '/Annual Report 2025-2026.pdf',
      fileSize: '171 MB',
      releaseDate: '',
      lastUpdated: ''
    },
    {
      id: '2024-25',
      year: '2024-25',
      titleEn: 'Annual Report 2024-25',
      titleMr: 'वार्षिक अहवाल २०२४-२५',
      descriptionEn: '23rd Annual Report of Shivpratap Multistate Institution',
      descriptionMr: 'शिवप्रताप मल्टीस्टेट संस्थेचा २३वा वार्षिक अहवाल',
      fileName: 'shivpratap_annual_report_2024_25.pdf',
      pdfPath: annualReportPDF,
      fileSize: '5.8 MB',
      releaseDate: '',
      lastUpdated: ''
    }
  ];

  const handleViewPDF = (report) => {
    setLoadingId(`view-${report.id}`);

    setTimeout(() => {
      setLoadingId(null);
      window.open(report.pdfPath, '_blank', 'noopener,noreferrer');
    }, 800);
  };

  const handleDownloadPDF = (report) => {
    setLoadingId(`download-${report.id}`);

    setTimeout(() => {
      setLoadingId(null);

      const link = document.createElement('a');
      link.href = report.pdfPath;
      link.download = report.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-50 py-8 px-4 relative overflow-hidden">
      {/* Watermark Logo */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
        <img
          src={language === 'en' ? engLogo : shivpratapLogo}
          alt="Shivpratap Logo Watermark"
          className="w-1/2 h-auto transform scale-150"
        />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-600 to-pink-600 rounded-full shadow-2xl mb-6">
            <FaFilePdf className="text-white text-3xl" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t.pageTitle}
          </h1>
          <div className="h-1 w-24 bg-gradient-to-r from-rose-500 to-pink-500 mx-auto rounded-full mb-4"></div>
        </div>

        <div className="space-y-12">
          {reports.map((report) => (
            <div key={report.id} className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">
                      {language === 'en' ? report.titleEn : report.titleMr}
                    </h2>
                    <p className="text-rose-100">
                      {language === 'en' ? report.descriptionEn : report.descriptionMr}
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0 text-right">
                    {report.releaseDate && <div className="text-sm text-rose-200">{t.published} {report.releaseDate}</div>}
                    {report.lastUpdated && <div className="text-sm text-rose-200">{t.updated} {report.lastUpdated}</div>}
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Quick Action Cards */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => handleDownloadPDF(report)}
                    disabled={loadingId === `download-${report.id}`}
                    className="p-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-300 flex flex-col items-center justify-center disabled:opacity-60 shadow-md"
                  >
                    <FaDownload className="text-2xl mb-2" />
                    <span className="font-medium">{t.pdfDownload}</span>
                    <span className="text-sm opacity-90 mt-1">{report.fileSize}</span>
                  </button>

                  <button
                    onClick={() => handleViewPDF(report)}
                    disabled={loadingId === `view-${report.id}`}
                    className="p-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl hover:from-rose-600 hover:to-pink-600 transition-all duration-300 flex flex-col items-center justify-center disabled:opacity-60 shadow-md"
                  >
                    <FaEye className="text-2xl mb-2" />
                    <span className="font-medium">{t.onlineRead}</span>
                    <span className="text-sm opacity-90 mt-1">{t.mobileComputer}</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnnualReport;